/**
 * Sprint 22 — ScopeIndicator.
 *
 * Pill at the top of the chat panel showing what the chat is "asking about",
 * with a dropdown to switch between the route's focus ("this thing") and the
 * whole workspace ("everything") — the Q3.1 toggle. Visible scope = trust.
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useChatRail, type ChatScope } from "./ChatRailContext";

function scopeLabel(s: ChatScope): string {
  if (s.label) return s.label;
  return s.kind === "workspace" ? "This workspace" : `This ${s.kind}`;
}

function sameScope(a: ChatScope, b: ChatScope): boolean {
  return a.kind === b.kind && (a.id ?? "") === (b.id ?? "");
}

export function ScopeIndicator() {
  const { scope, routeScope, setActiveScope } = useChatRail();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const workspaceScope: ChatScope = { kind: "workspace", label: "This workspace" };
  const options: ChatScope[] = [workspaceScope];
  if (routeScope.kind !== "workspace") options.unshift(routeScope);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={pill}
        title="Switch what the chat is asking about"
      >
        <i className="ti ti-target" style={{ fontSize: 13, flexShrink: 0 }} aria-hidden="true" />
        <span style={{ flex: "0 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          Asking about {scopeLabel(scope)}
        </span>
        <i className="ti ti-chevron-down" style={{ fontSize: 13, opacity: 0.6, flexShrink: 0 }} aria-hidden="true" />
      </button>

      {open && (
        <div style={menu} role="menu">
          {options.map((opt) => {
            const active = sameScope(opt, scope);
            return (
              <button
                key={`${opt.kind}:${opt.id ?? ""}`}
                type="button"
                role="menuitem"
                onClick={() => {
                  // Switching to the route scope re-attaches its page context.
                  setActiveScope(opt, opt.kind === routeScope.kind ? undefined : null);
                  setOpen(false);
                }}
                style={{ ...menuItem, ...(active ? menuItemActive : null) }}
              >
                <span>{scopeLabel(opt)}</span>
                {active && <i className="ti ti-check" style={{ fontSize: 13 }} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const pill: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px",
  borderRadius: "var(--border-radius-pill)",
  border: "var(--kuvio-border-default)",
  background: "var(--color-background-secondary)",
  color: "var(--color-text-secondary)",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  cursor: "pointer",
  minWidth: 0,
  maxWidth: "100%",
};

const menu: CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  minWidth: 200,
  background: "var(--color-background-primary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-md)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  padding: 4,
  zIndex: 50,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const menuItem: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "6px 8px",
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "transparent",
  color: "var(--color-text-primary)",
  fontSize: 13,
  fontFamily: "var(--font-sans)",
  cursor: "pointer",
  textAlign: "left",
  width: "100%",
};

const menuItemActive: CSSProperties = {
  color: "var(--color-text-info)",
  background: "var(--color-background-secondary)",
};
